<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import IconAnimatedQr from "#/assets/iconsDynamic/AnimatedQr.vue";
import IconAnimatedSuccess from "#/assets/iconsDynamic/AnimatedSuccess.vue";
import { useHidScanner } from "#/composables/useHidScanner";
import { useModal } from "#/composables/useModal";
import { vAutoFocus } from "#/directives/vAutoFocus";
import { useThemeStore } from "#/store/theme";

type View = "qr" | "code" | "success" | null;
type Source = "qr" | "code" | null;

const SUCCESS_TIMEOUT = 2000;
const MIN_CODE_LENGTH = 4;

const emit = defineEmits<(e: "confirm", value: string) => void>();

const { t } = useTranslation();
const { visible, close } = useModal("loyalty-plant-scanner");

let successTimeout: ReturnType<typeof setTimeout> | null = null;

const themeStore = useThemeStore();

const view = ref<View>("qr");
const source = ref<Source>(null);
const code = ref("");

const isSkannerEnabled = computed(() => !!themeStore.data?.kiosk_loyalty_plant_scanner);
const isManualEnabled = computed(() => !!themeStore.data?.kiosk_loyalty_plant_manual);

const canSubmit = computed(() => code.value.trim().length >= MIN_CODE_LENGTH);
const successTitle = computed(() => {
	if (source.value === "qr") return t("modal.loyalty-plant-scanner.success.qr");
	if (source.value === "code") return t("modal.loyalty-plant-scanner.success.code");
	return "";
});

const getInitialView = (): View => {
	if (isSkannerEnabled.value) return "qr";
	if (isManualEnabled.value) return "code";
	return "qr";
};

const reset = () => {
	view.value = getInitialView();
	code.value = "";
	source.value = null;
	if (successTimeout) {
		clearTimeout(successTimeout);
		successTimeout = null;
	}
};

const toCode = () => {
	if (!isManualEnabled.value) return;
	view.value = "code";
	code.value = "";
};

const toQr = () => {
	if (!isSkannerEnabled.value) return;
	view.value = "qr";
	code.value = "";
};

const onClose = () => {
	reset();
	close();
};

const onSuccess = (value: string, from: Exclude<Source, null>) => {
	if (view.value === "success") return;
	source.value = from;
	view.value = "success";
	emit("confirm", value);
	successTimeout = setTimeout(onClose, SUCCESS_TIMEOUT);
};

const onCodeInput = (e: Event) => {
	const target = e.target as HTMLInputElement;
	code.value = target.value.toUpperCase().slice(0, 16);
};

const submitCode = () => {
	if (!isManualEnabled.value) return;
	const value = code.value.trim().toUpperCase();
	if (value.length < MIN_CODE_LENGTH) return;
	onSuccess(value, "code");
};

const finish = () => {
	if (successTimeout) clearTimeout(successTimeout);
	onClose();
};

const onScan = (scanned: string) => {
	if (view.value !== "qr") return;
	onSuccess(scanned, "qr");
};

const _onKeyDown = (e: KeyboardEvent) => {
	if (!visible.value || view.value !== "qr") return;
	onKeyDown(e);
};

const { onKeyDown } = useHidScanner({ onScan, minLength: 3, maxKeyInterval: 90, registerListener: false });

watch(visible, (isVisible) => {
	if (isVisible) reset();
});

onMounted(() => window.addEventListener("keydown", _onKeyDown));
onUnmounted(() => {
	if (successTimeout) clearTimeout(successTimeout);
	window.removeEventListener("keydown", _onKeyDown);
});
</script>

<template>
	<Transition name="fade-in-up">
		<div v-if="visible" class="loyalty_plant_scanner_modal_overlay" @click.self="onClose">
			<div class="loyalty_plant_scanner_modal_content">
				<div class="loyalty_plant_scanner_modal_header">
					<h2 class="loyalty_plant_scanner_modal_title">{{ t(`card-reader.status.wait.text`) }}</h2>

					<button type="button" class="loyalty_plant_scanner_modal_close" @click="onClose">
						{{ t(`card-reader.button.close`) }}
					</button>
				</div>

				<div class="loyalty_plant_scanner_modal_body">
					<Transition name="lps-fade" mode="out-in">
						<div v-if="view === 'qr'" key="qr" class="loyalty_plant_scanner_qr">
							<p class="loyalty_plant_scanner_heading">{{ t("modal.loyalty-plant-scanner.qr.title") }}</p>
							<p class="loyalty_plant_scanner_sub">{{ t("modal.loyalty-plant-scanner.qr.subtitle") }}</p>

							<IconAnimatedQr class="loyalty_plant_scanner_frame" />

							<template v-if="isManualEnabled">
								<div class="loyalty_plant_scanner_divider">
									<span />
									<span>{{ t("modal.loyalty-plant-scanner.qr.or") }}</span>
									<span />
								</div>

								<button type="button" class="loyalty_plant_scanner_manual" @click="toCode">
									{{ t("modal.loyalty-plant-scanner.qr.manual") }}
								</button>
							</template>
						</div>

						<div v-else-if="view === 'code'" key="code" class="loyalty_plant_scanner_code">
							<p class="loyalty_plant_scanner_code_hint">{{ t("modal.loyalty-plant-scanner.code.hint") }}</p>

							<label class="loyalty_plant_scanner_input_wrap">
								<input
									:value="code"
									type="text"
									class="loyalty_plant_scanner_input"
									:placeholder="t('modal.loyalty-plant-scanner.code.placeholder')"
									autocomplete="off"
									autocapitalize="characters"
									spellcheck="false"
									maxlength="16"
									v-auto-focus
									@input="onCodeInput"
									@keydown.enter.prevent="submitCode"
								>
							</label>

							<button type="button" class="loyalty_plant_scanner_submit" :disabled="!canSubmit" @click="submitCode">
								{{ t("card-reader.button.send") }}
							</button>

							<button v-if="isSkannerEnabled" type="button" class="loyalty_plant_scanner_back" @click="toQr">
								← {{ t("modal.loyalty-plant-scanner.code.back") }}
							</button>
						</div>

						<div v-else-if="view === 'success'" key="success" class="loyalty_plant_scanner_success">
							<IconAnimatedSuccess />
							<p class="loyalty_plant_scanner_heading">{{ successTitle }}</p>

							<button type="button" class="loyalty_plant_scanner_continue" @click="finish">
								{{ t("modal.loyalty-plant-scanner.success.continue") }}
							</button>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
@use "./loyaltyPlantScanner.scss";
</style>
